import { Inject, Injectable, DOCUMENT } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  interval,
  map,
  Observable,
  switchMap,
  take,
  timestamp,
} from 'rxjs';
import Vapi from '@vapi-ai/web';
import moment from 'moment';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from './common.service';
import { ExaminationService } from 'src/app/examination/examination.service';

import { AppClient } from '../../../environments/client';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';
import { DateUtil } from '../utils/date.util';
import { LoggedInUserKeys, LoggedInUserService } from './logged-in-user.service';
import { AppUsersDto } from '../model/app-users-dto';
import { DebriefProfileTimerService } from './debrief-profile-timer.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  type?: 'text' | 'preview' | 'pdf' | 'suggestion' | 'file' | 'resume-results';
  text?: string;
  previewMarkDown?: string;
  downloadPdf?: string;
  searchedResumesMatches?:string;

  attachment?: {
    name: string;
    url: string;
    uploadedFile: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class VoiceAssistantService {
  vapi: Vapi;
  private activeCallId: string | null = null;
  soundWavesState$ = new BehaviorSubject<boolean>(false);
  isOngoingCall$ = new BehaviorSubject<boolean>(false);
  isAssistantSpeaking$ = new BehaviorSubject<boolean>(false);
  callStartingLoadingState$ = new BehaviorSubject<boolean>(false);
  showVoiceCallSection$ = new BehaviorSubject<boolean>(false);
  messages$ = new BehaviorSubject<ChatMessage[]>([]);
  showQueueScreen$ = new BehaviorSubject<boolean>(false);
  // True from the moment a debrief call ends until the app leaves the bot page,
  // so the page stops treating fullscreen exit / reload as a "leave" attempt.
  debriefInterviewCompleted$ = new BehaviorSubject<boolean>(false);
  private isDefaultViewSubject = new BehaviorSubject<boolean>(true);
  public isDefaultView$ = this.isDefaultViewSubject.asObservable();
  private eventsRegistered = false;
  private assistantBuffer: string[] = [];
  private botFirstMessageSent = false;
  private userFirstMessageSent = false;
  private interviewEndedEventSent = false;
  private transcriptEventSent = false;
  // Set when vapi's own 'call-end' fires (natural completion). The component
  // that then navigates away still tears down and calls stopVapiCall() again
  // - this lets that redundant call log the real reason instead of 'unknown'.
  private callEndedNaturally = false;
  private messagesSubscription: any;
  private initializingVapiClient: Promise<boolean> | null = null;

  assistantId: string;
  driveId: string;
  candidateId: string;
  interviewStarted: boolean = false;
  interviewEnded: boolean = false;
  assistantOveride: any;
  userAssessmentDriveSetId: string;
  isBotEndedDueToSilence: boolean = false;
  deployedClient = environment.deployedClient;
  isDebriefBot:boolean = false;
  isPMBuddyBot:boolean = false;
  loginUser: AppUsersDto;


  constructor(
    private _router: Router,
    private _commonService: CommonService,
    private _examinationService: ExaminationService,
    private _actRoute: ActivatedRoute,
    private loggedInUserService: LoggedInUserService,
    private readonly debriefProfileTimer: DebriefProfileTimerService,
    @Inject(DOCUMENT) private document: any,
  ) {
    // if (
    //   this.deployedClient == AppClient.PROD_MAIN ||
    //   this.deployedClient == AppClient.PROD_LITE
    // ) {
    //   this.vapi = new Vapi(
    //     environment.VAPI_INTERVIEW_AGENT_PUBLIC_KEY as string,
    //     `${environment.baseUrl}`,
    //   );
    // } else {
    //   this.vapi = new Vapi(
    //     environment.VAPI_INTERVIEW_AGENT_PUBLIC_KEY as string,
    //     `${environment.baseUrl}`,
    //   );
    // }
    this.initializeVapiClient();

    this.driveId = sessionStorage.getItem('driveId') || localStorage.getItem('driveId') || '';
    this.userAssessmentDriveSetId = sessionStorage.getItem('userAssessmentDriveSetId') || localStorage.getItem('userAssessmentDriveSetId') || '';
  }

  // Fetches a fresh VAPI token and (re)constructs the Vapi client. Called once
  // from the constructor, and again from startVapiCallInterviewAgent() if a
  // prior stopVapiCall() (e.g. leaving the bot page mid-drive) nulled `vapi` -
  // the singleton otherwise never gets a second chance to re-authenticate.
  private initializeVapiClient(): Promise<boolean> {
    if (this.initializingVapiClient) {
      return this.initializingVapiClient;
    }

    this.initializingVapiClient = new Promise((resolve) => {
      this._commonService.getVapiToken().subscribe({
        next: (response: any) => {
          const token = response?.data?.token || response?.token;

          if (token) {
            this.vapi = new Vapi(token, `${environment.baseUrl}`);
            console.log('VAPI initialized with fresh token successfully');
            Sentry.logger.info(
              'Interview Bot VAPI Initialized',
              this.logContext(),
            );
            if (!this.eventsRegistered) {
              this.handleVapiEvents();
              this.eventsRegistered = true;
            }
            this.initializingVapiClient = null;
            resolve(true);
          } else {
            console.error('VAPI token missing in response, cannot initialize Vapi', response);
            Sentry.logger.error(
              'Interview Bot VAPI Token Missing In Response',
              this.logContext(),
            );
            this.initializingVapiClient = null;
            resolve(false);
          }
        },
        error: (err) => {
          console.error('Failed to fetch VAPI token', err);
          Sentry.logger.error(
            'Interview Bot VAPI Token Fetch Failed',
            this.logContext({ errorMessage: String(err?.message ?? err) }),
          );
          Sentry.captureException(err, {
            tags: { stage: 'interview-bot-vapi-token-fetch', driveId: this.driveId },
          });
          this.initializingVapiClient = null;
          resolve(false);
        },
      });
    });

    return this.initializingVapiClient;
  }

  // Every Sentry log line for the interview bot carries this same identifying
  // context so an event can be traced end-to-end from Sentry alone, without
  // cross-referencing source code or session storage.
  private logContext(extra: Record<string, string> = {}) {
    return {
      driveId: String(this.driveId ?? ''),
      candidateId: String(this.candidateId ?? ''),
      assistantId: String(this.assistantId ?? ''),
      callId: String(this.activeCallId ?? ''),
      userAssessmentDriveSetId: String(this.userAssessmentDriveSetId ?? ''),
      userInterviewSessionId: String(
        sessionStorage.getItem('userInterviewSessionId') ?? '',
      ),
      ...extra,
    };
  }

  handleVapiEvents() {
    this.vapi.on('call-start', () => {
      Sentry.logger.info('Interview Call Started', this.logContext());
      this.conversationLength = 0;
      this.eventMessagesLength = 0;
      this.processedToolCallIds.clear();
      Sentry.logger.info('Interview Call Started', {
        driveId: String(this.driveId),
        assistantId: String(this.assistantId),
      });
       if(this.isPMBuddyBot) {        
        const payload = {
        assistantId: this.assistantId,
        callId: this.activeCallId,
        interviewStarted: true, 
        email: this.loginUser?.email,
        assistantOverrides: this.assistantOveride,
      };
      this._examinationService
        .saveJDBotData(payload)
        .subscribe((res) => {});
    } else {
        const payload = {
        driveId: this.driveId,
        assistantId: this.assistantId,
        callId: this.activeCallId,
        interviewStarted: true,
        assistantOverrides: this.assistantOveride,
        userAssessmentDriveSetId: this.userAssessmentDriveSetId,
        userInterviewSessionId: sessionStorage.getItem('userInterviewSessionId') || localStorage.getItem('userInterviewSessionId'),
      };
      this._examinationService
        .saveBotInterviewResponse(payload)
        .subscribe((res) => {});
    }
      
      this.isOngoingCall$.next(true);
      this.callStartingLoadingState$.next(false);
      this.showVoiceCallSection$.next(true);
      // this.activeCallId = (this.vapi as any).call?.id ?? null;
    });

    this.vapi.on('call-end', () => {
      // The success page reloads the app, which would abort this request, so the
      // debrief flow waits for it to settle before navigating there.
      const debriefDetailsCheck = this.isDebriefBot
        ? this.checkDebriefInterviewDetails()
        : null;
      if (this.isDebriefBot) {
        this.debriefInterviewCompleted$.next(true);
      }
      this.callEndedNaturally = true;
      Sentry.logger.info(
        'Interview Call Ended',
        this.logContext({
          endedDueToSilence: String(this.isBotEndedDueToSilence),
        }),
      );
      if(!this.isBotEndedDueToSilence) {
        this.sendWebEvent(
          'botEndMessage',
          'Interview has ended successfully. Bot said goodbye to candidate.',
        );
      }
      this.sendTranscriptEvent();
      if (this.messagesSubscription) {
        this.messagesSubscription.unsubscribe();
        this.messagesSubscription = null;
      }
      this.showVoiceCallSection$.next(false);
      this.callStartingLoadingState$.next(false);
      this.isOngoingCall$.next(false);
      this.isAssistantSpeaking$.next(false);
      this.soundWavesState$.next(false);
      this.activeCallId = null;
      this.conversationLength = 0;
      this.eventMessagesLength = 0;
      this.processedToolCallIds.clear();
      this.exitFullScreen();
      this.messages$.next([]);
      this.vapi.removeAllListeners();
      this.eventsRegistered = false;

      const shouldShowFeedbackModal = !this.isDebriefBot;
      if (shouldShowFeedbackModal) {
        this._commonService.isFeedbackModalVisible.next(true);
        sessionStorage.setItem('isFeedbackModalVisible', 'true');
      } else {
        this._commonService.isFeedbackModalVisible.next(false);
        sessionStorage.removeItem('isFeedbackModalVisible');
      }

      this.isDebriefBot = false;

      if(this.isPMBuddyBot) {
        this.setIsDefaultView(true);
      } else if (debriefDetailsCheck) {
        // Start the shared 2-minute "fetching profile" countdown only once the
        // success page is reached, so the API wait doesn't eat into it.
        debriefDetailsCheck
          .then(() =>
            this._router.navigate([
              this._commonService.Navigations.SUBMIT_SUCCESS_PAGE,
            ]),
          )
          .then(() => this.debriefProfileTimer.start());
      } else {
        this._router.navigate([
        this._commonService.Navigations.SUBMIT_SUCCESS_PAGE,
      ]);
      }
    });

    this.vapi.on('speech-start', () => {
      this.isAssistantSpeaking$.next(true);
    });

    this.vapi.on('speech-end', () => {
      this.isAssistantSpeaking$.next(false);
    });

    const toolCallsHandler = (msg: any) => {
      if (msg?.function?.name) {
        const toolName = msg.function.name;
        const toolArgs = msg.function.arguments ? 
          JSON.parse(msg.function.arguments) : {};
        
        const toolPayload = {
          tool: toolName,
          ...toolArgs
        };
        const toolMessage = this.buildToolMessage(toolPayload);
        if (toolMessage) {
          this.appendAssistantMessage(toolMessage);
          console.log(`%c[tool-calls handler] Added message`, 'background: #95e1d3; color: black', toolMessage);
        } else {
          console.warn(`%c[tool-calls handler] buildToolMessage returned null for ${toolName}`, 'background: #ffcc00; color: black');
        }
      }
    };
    (this.vapi as any).on('tool-calls', toolCallsHandler);
    const toolCallResultHandler = (msg: any) => {      
      if (msg?.name && msg?.result) {
        const toolName = msg.name;
        const resultData = typeof msg.result === 'string' ? JSON.parse(msg.result) : msg.result;
        
        const toolPayload = {
          name: toolName,
          tool: toolName,  // Use the outer tool name (e.g., generatePDF2)
          ...resultData,   // Merge in the result data (which may have url, content, etc.)
          originalToolName: resultData?.tool  // Keep track of the inner tool name if different
        };
        const toolMessage = this.buildToolMessage(toolPayload);
        if (toolMessage) {
          this.appendAssistantMessage(toolMessage);
        } else {
          console.warn(`%c[tool-call-result handler] buildToolMessage returned null for ${toolName}`, 'background: #ffcc00; color: black');
        }
      }
    };
    (this.vapi as any).on('tool-call-result', toolCallResultHandler);

    this.vapi.on('message', (msg: any) => {
  switch (msg.type) {
    case 'model-output':
      this.handleModelOutput(msg);
      break;

    case 'transcript':
      this.handleTranscript(msg);
      break;

    case 'speech-update':
      this.handleSpeechUpdate(msg);
      break;

    case 'status-update':
      this.handleStatusUpdate(msg);
      break;

    case 'conversation-update':
      this.handleConversationUpdate(msg);
      break;

    case 'tool-result':
      this.handleToolResult(msg);
      break;

    default:
      // console.warn('Unhandled VAPI message:', msg);
  }
});
  }

  /* -----------------------
       Handlers
    ------------------------ */
  handleModelOutput(msg: any) {
    if (typeof msg.output !== 'string') return;

    const current = this.messages$.getValue();
    const latest = current[current.length - 1];
    const incoming = msg.output;

    if (latest && latest.role === 'assistant') {
      const existing = latest.text || '';

      // Support both delta and cumulative stream chunks without duplicating words.
      if (!existing) {
        latest.text = incoming;
      } else if (incoming.startsWith(existing)) {
        latest.text = incoming;
      } else if (!existing.endsWith(incoming)) {
        latest.text = existing + incoming;
      }

      this.messages$.next([...current]);
    } else {
      // create new assistant message
      this.messages$.next([
        ...current,
        { role: 'assistant', type: 'text', text: incoming },
      ]);
    }
  }

   handleTranscript(msg: any) {
    // console.log(msg,'msg');
    
    if (msg.transcriptType !== 'final' || msg.role !== 'user') return;

    const incoming = (msg.transcript || '').trim();
    if (!incoming) return;

    const current = this.messages$.getValue();
    const latest = current[current.length - 1];

    if (latest && latest.role === 'user') {
      const existing = (latest.text || '').trim();
      const normalize = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();

      // Ignore duplicated final transcript for the same user text.
      if (normalize(existing) === normalize(incoming)) {
        return;
      }

      // Handle cumulative transcript snapshots without duplicating words.
      if (normalize(incoming).startsWith(normalize(existing))) {
        latest.text = incoming;
      } else {
        latest.text = `${existing} ${incoming}`.trim();
      }

      this.messages$.next([...current]);
    } else {
      // create new user message
      this.messages$.next([...current, { role: 'user', type: 'text', text: incoming }]);
    }
  }

   handleSpeechUpdate(msg: any) {
    const { role, status } = msg;

    if (role === 'assistant') {
      this.isAssistantSpeaking$.next(status === 'started');
    }

    if (role === 'user') {
      this.soundWavesState$.next(status === 'started');
    }
  }

conversationLength: number = 0;
eventMessagesLength: number = 0;
 processedToolCallIds = new Set<string>();
 appendAssistantMessage(message: ChatMessage): void {
  console.log('[appendAssistantMessage] Adding message:', message);
  const current = this.messages$.getValue();
  const latest = current[current.length - 1];

  const isDuplicate =
    latest?.role === message.role &&
    latest?.type === message.type &&
    latest?.text === message.text &&
    latest?.previewMarkDown === message.previewMarkDown &&
    latest?.downloadPdf === message.downloadPdf;

  if (isDuplicate) {
    console.log('[appendAssistantMessage] Skipping duplicate message');
    return;
  }

  console.log('[appendAssistantMessage] Current messages count:', current.length);
  this.messages$.next([...current, message]);
  const updated = this.messages$.getValue();
  console.log('[appendAssistantMessage] Updated messages count:', updated.length);
  console.log('[appendAssistantMessage] Full messages:', updated);
 }

  parseToolPayload(rawContent: any): any | null {
  try {
    return typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
  } catch {
    return null;
  }
 }

  buildToolMessage(payload: any): ChatMessage | null {
  const toolName = payload?.tool || payload?.name;
  let toolContent = payload?.content;
  let toolUrl = payload?.url;
  
  if (!toolContent && payload?.result && typeof payload.result === 'object') {
    toolContent = payload.result.content;
    toolUrl = toolUrl || payload.result.url;
  }
  
  // Also check if result is a string that might contain URL
  if (!toolUrl && typeof payload?.result === 'string') {
    try {
      const parsed = JSON.parse(payload.result);
      toolUrl = toolUrl || parsed.url;
      toolContent = toolContent || parsed.content;
    } catch (e) {
      // result is plain string, not JSON
    }
  }

  // === PREVIEW TOOL ===
  if (toolName === 'previewTool' && toolContent) {
    console.log(`[buildToolMessage] Creating preview message`);
    return {
      role: 'assistant',
      type: 'preview',
      text: 'Preview',
      previewMarkDown: toolContent,
    };
  }

  // === GENERATE PDF TOOL ===
  // Handle both 'generatePDF' and 'generatePDF2' tool names
  const isPdfTool = toolName === 'generatePDF' || toolName === 'generatePDF2' || toolName?.toLowerCase?.().includes('generatepdf');
  if (isPdfTool) {
    console.log(`[buildToolMessage] PDF tool detected. toolName: ${toolName}, toolUrl: ${toolUrl}, toolContent: ${toolContent}`);
    
    if (toolUrl) {
      console.log(`[buildToolMessage] Creating PDF download message with URL: ${toolUrl}`);
      return {
        role: 'assistant',
        type: 'pdf',
        // text: 'Download Final Job Description',
        downloadPdf: toolUrl,
      };
    }

    if (typeof payload?.result === 'string') {
      const normalizedResult = payload.result.toLowerCase();

      if (normalizedResult.includes('still pending')) {
        console.log(`[buildToolMessage] PDF generation pending (string result)`);
        return {
          role: 'assistant',
          type: 'text',
          text: 'Generating final PDF... please wait.',
        };
      }

      if (normalizedResult.includes('error')) {
        console.log(`[buildToolMessage] PDF generation error (string result)`);
        return {
          role: 'assistant',
          type: 'text',
          text: 'Unable to generate the final PDF right now. Please try again.',
        };
      }
    }
    
    // Handle pending state
    if (typeof toolContent === 'string' && toolContent.toLowerCase().includes('still pending')) {
      console.log(`[buildToolMessage] PDF generation pending`);
      return {
        role: 'assistant',
        type: 'text',
        text: 'Generating final PDF... please wait.',
      };
    }
    
    // Handle error state
    if (typeof toolContent === 'string' && toolContent.toLowerCase().includes('error')) {
      console.log(`[buildToolMessage] PDF generation error`);
      return {
        role: 'assistant',
        type: 'text',
        text: 'Unable to generate the final PDF right now. Please try again.',
      };
    }
  }

  // === SUGGESTION TOOL ===
  if (toolName === 'showSuggestionTool' && toolContent) {
    console.log(`[buildToolMessage] Creating suggestion message`);
    return {
      role: 'assistant',
      type: 'suggestion',
      text: 'Suggestions',
      previewMarkDown: toolContent,
    };
  }

  // === RESUME SEARCH ===
  if (toolName === 'search_resumes_by_jd' && payload?.results?.length) {
    console.log(`[buildToolMessage] Creating resume search results message`);
    return {
      role: 'assistant',
      type: 'resume-results',
      text: 'Here are the matched candidates for this JD',
      searchedResumesMatches: payload.results,
    };
  }

  console.log(`[buildToolMessage] No match for tool: ${toolName}`);
  return null;
 }

    handleConversationToolItem(item: any): void {
    if (!item?.role) return;

    if (item?.toolCallId && this.processedToolCallIds.has(item.toolCallId)) {
      return;
    }

    if (item?.role === 'tool_call_result') {
      // Extract tool name - could be at item.name or in item.result
      const toolName = item?.name;
      let resultUrl = item?.result?.url;
      let resultContent = item?.result?.content;
      
      // If result is a string, try parsing it
      if (!resultUrl && typeof item?.result === 'string') {
        try {
          const parsed = JSON.parse(item.result);
          resultUrl = parsed.url;
          resultContent = parsed.content;
        } catch (e) {
          // Not JSON
        }
      }
      
      const payload = {
        name: toolName,
        tool: item?.result?.tool || toolName,
        result: item?.result,
        url: resultUrl,
        content: resultContent,
      };

      const toolMessage = this.buildToolMessage(payload);
      if (toolMessage) {
        this.appendAssistantMessage(toolMessage);
        if (item?.toolCallId) {
          this.processedToolCallIds.add(item.toolCallId);
        }
      }
      return;
    }

    if (item?.role === 'tool_calls' && Array.isArray(item?.toolCalls)) {
      item.toolCalls.forEach((toolCall: any) => {
        const toolName = toolCall?.function?.name;
        const parsedArgs = this.parseToolPayload(toolCall?.function?.arguments) || {};
        const payload = { tool: toolName, name: toolName, ...parsedArgs };
        const toolMessage = this.buildToolMessage(payload);
        if (toolMessage) {
          this.appendAssistantMessage(toolMessage);
        }
      });
      return;
    }

    if (item?.role === 'tool') {
      const parsedContent = this.parseToolPayload(item?.content);
      if (!parsedContent) {
        return;
      }

      const toolMessage = this.buildToolMessage(parsedContent);
      if (toolMessage) {
        this.appendAssistantMessage(toolMessage);
      }
    }
   }

 handleConversationUpdate(message: any): void {
  console.log('[handleConversationUpdate] Received conversation-update event:', message);

  const conversation = message?.conversation;
  const eventMessages = message?.messages;
  const conversationNewLength = conversation?.length ?? 0;
  const eventMessagesNewLength = eventMessages?.length ?? 0;

  const hasConversationDelta = conversationNewLength > this.conversationLength;
  const hasMessagesDelta = eventMessagesNewLength > this.eventMessagesLength;

  if (!hasConversationDelta && !hasMessagesDelta) return;

  if (hasConversationDelta) {
    const conversationStartIndex = this.conversationLength;
    this.conversationLength = conversationNewLength;

    for (let i = conversationStartIndex; i < conversationNewLength; i++) {
      const conversationItem = conversation?.[i];
      console.log('[handleConversationUpdate] Processing conversation item:', conversationItem);
      this.handleConversationToolItem(conversationItem);
    }
  }

  if (hasMessagesDelta) {
    const eventMessagesStartIndex = this.eventMessagesLength;
    this.eventMessagesLength = eventMessagesNewLength;

    for (let i = eventMessagesStartIndex; i < eventMessagesNewLength; i++) {
      const eventMessageItem = eventMessages?.[i];
      console.log('[handleConversationUpdate] Processing messages item:', eventMessageItem);
      this.handleConversationToolItem(eventMessageItem);
    }
  }
 }

 handleToolResult(message: any): void {
  console.log('[handleToolResult] Received tool-result event:', message);
  
  const toolMessage = this.buildToolMessage(message);
  if (toolMessage) {
    this.appendAssistantMessage(toolMessage);
    console.log('[handleToolResult] Tool message added to transcript:', toolMessage);
    return;
  }

  // Fallback for unhandled tool types
  console.warn('[handleToolResult] Unhandled tool type:', message?.tool, 'Full payload:', message);
  this.appendAssistantMessage({
    role: 'assistant',
    type: 'text',
    text: 'Received tool response (unhandled type).'
  });
}

  // private isInterviewEndMessage(text: string): boolean {
  //   const normalized = text?.toLowerCase()?.replace(/\s+/g, ' ').trim();
  //   return (
  //     normalized.includes('thank you for your time today') &&
  //     normalized.includes('pleasant day ahead') &&
  //     normalized.includes('good bye')
  //   );
  // }

  private setupMessagesMonitoring() {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }

    this.messagesSubscription = this.messages$.subscribe((messages) => {
      // Check for bot first message
      // if (!this.botFirstMessageSent && messages.length > 0) {
      //   const firstAssistantMsg = messages.find((m) => m.role === 'assistant');
      //   if (firstAssistantMsg) {
      //     console.log('Bot first message detected:', firstAssistantMsg.text);
      //     this.botFirstMessageSent = true;
      //     this.sendWebEvent('botFirstMessage', firstAssistantMsg.text);
      //   }
      // }

      // Check for user first message
      if (!this.userFirstMessageSent && messages.length > 0) {
        const firstUserMsg = messages.find((m) => m.role === 'user');
        if (firstUserMsg) {
          // console.log('User first message detected:', firstUserMsg.text);
          this.userFirstMessageSent = true;
          this.sendWebEvent(
            'userFirstMessage',
            'Candidate sent the first message to bot after interview started.',
          );
        }
      }

      // Check for interview end message (bot goodbye)
      // if (!this.interviewEndedEventSent && messages.length > 0) {
      //   const lastAssistantMsg = [...messages]
      //     .reverse()
      //     .find((m) => m.role === 'assistant');
      //   if (
      //     lastAssistantMsg &&
      //     this.isInterviewEndMessage(lastAssistantMsg.text)
      //   ) {
      //     console.log(
      //       'Interview ended message detected:',
      //       lastAssistantMsg.text,
      //     );
      //     this.interviewEndedEventSent = true;
      //     this.sendWebEvent('interviewEnded', 'Interview has ended. Bot said goodbye to candidate.');
      //   }
      // }
    });
  }

  sendWebEvent(eventName: string, eventValue: string) {
    if (!this.activeCallId) {
      // console.warn(
      //   'Unable to send web event, active call id missing:',
      //   eventName,
      // );
      return;
    }

    const payload = {
      callId: this.activeCallId,
      event: {
        [eventName]: eventValue,
        timestamp: DateUtil.getCurrentIST(),
      },
    };

    this._examinationService.callWebEvent(payload).subscribe({
      next: () => {
        // console.log(`Web event sent: ${eventName}`);
      },
      error: (err) => {
        // console.error(`Failed to call web event ${eventName}`, err);
      },
    });
  }

  private sendInterviewEndedEvent(message: string) {
    if (this.interviewEndedEventSent) return;
    this.interviewEndedEventSent = true;
    this.sendWebEvent('interviewEnded', message);
  }

  private sendTranscriptEvent() {
    if (this.transcriptEventSent) return;
    this.transcriptEventSent = true;

    const transcript = this.messages$
      .getValue()
      .map((item) => `${item.role}: ${item.text}`)
      .join('\n');

    this.sendWebEvent('transcript', transcript);
  }

  async startVapiCallInterviewAgent(callConfig: any) {
    // console.log(callConfig, 'callConfig');

    this.botFirstMessageSent = false;
    this.userFirstMessageSent = false;
    this.interviewEndedEventSent = false;
    this.transcriptEventSent = false;
    this.callEndedNaturally = false;

    this.callStartingLoadingState$.next(true);

    const { assistantId, config } = callConfig.payloadRequest;

    // Populate identifying context up front so every downstream log line
    // (queue checks, call-start/end, status updates, errors) can be traced
    // back to this drive/candidate/assistant without touching source code.
    this.driveId = callConfig.driveId ?? this.driveId;
    this.candidateId = callConfig.candidateId;
    this.assistantId = assistantId;
    this.userAssessmentDriveSetId =
      sessionStorage.getItem('userAssessmentDriveSetId') ?? this.userAssessmentDriveSetId;

    Sentry.logger.info('Interview Bot Call Start Requested', this.logContext());

    if (!this.vapi) {
      const initialized = await this.initializeVapiClient();
      if (!initialized) {
        Sentry.logger.error(
          'Interview Bot VAPI Re-Initialization Failed',
          this.logContext(),
        );
        this.callStartingLoadingState$.next(false);
        return { call: null, status: 'error' };
      }
    }

    let call;
    try {
      call = await this.vapi.start(assistantId, {
        ...config,
        metadata: {
          driveId: callConfig.driveId,
          candidateId: callConfig.candidateId,
        },
      });
    } catch (err) {
      console.error('vapi.start failed:', err);
      Sentry.logger.error(
        'Interview Bot Call Start Failed',
        this.logContext({ errorMessage: String(err?.message ?? err) }),
      );
      Sentry.captureException(err, {
        tags: {
          stage: 'interview-bot-vapi-start',
          driveId: this.driveId,
          candidateId: this.candidateId,
        },
      });
      this.callStartingLoadingState$.next(false);
      return { call: null, status: 'error' };
    }

    // console.log('Call started:', call);

    this.activeCallId = call?.id ?? null;
    if (this.activeCallId) {
      sessionStorage.setItem('activeCallId', this.activeCallId);
    }

    this.assistantOveride = { ...config };

    const isQueued =
      (call as any)?.subscriptionLimits?.remainingConcurrentCalls <= 0;

    // console.log(
    //   'Initial queue check - remainingConcurrentCalls:',
    //   (call as any)?.subscriptionLimits?.remainingConcurrentCalls,
    //   'isQueued:',
    //   isQueued,
    // );

    if (isQueued) {
      Sentry.logger.warn(
        'Interview Bot Queued (initial check)',
        this.logContext({
          remainingConcurrentCalls: String(
            (call as any)?.subscriptionLimits?.remainingConcurrentCalls,
          ),
        }),
      );
      this.vapi.stop();
      this.vapi.removeAllListeners();
      this.eventsRegistered = false;
      this.activeCallId = null;
      this.showQueueScreen$.next(true);
      this.callStartingLoadingState$.next(false);
      return { call, status: 'queued', queued: true };
    }

    if (!this.eventsRegistered) {
      this.handleVapiEvents();
      this.eventsRegistered = true;
    }
    this.setupMessagesMonitoring();

    if (this.activeCallId != null) {
      try {
        const status = await firstValueFrom(
          this.checkQueueStatus(this.activeCallId),
        );

        console.log('Async queue status:', status);

        if (status === 'queued') {
          Sentry.logger.warn(
            'Interview Bot Queued (async check)',
            this.logContext(),
          );
          this.vapi.stop();
          this.vapi.removeAllListeners();
          this.eventsRegistered = false;
          this.activeCallId = null;
          this.showQueueScreen$.next(true);
          this.callStartingLoadingState$.next(false);
          return { call, status, queued: true };
        }

        this.showQueueScreen$.next(false);
      } catch (err) {
        console.error('Error calling checkQueueStatus:', err);
        Sentry.logger.error(
          'Interview Bot Queue Status Check Failed',
          this.logContext({ errorMessage: String(err?.message ?? err) }),
        );
        Sentry.captureException(err, {
          tags: {
            stage: 'interview-bot-queue-status-check',
            driveId: this.driveId,
            candidateId: this.candidateId,
            callId: this.activeCallId,
          },
        });
        this.callStartingLoadingState$.next(false);
        return { call, status: 'error' };
      }
    }

    Sentry.logger.info('Interview Bot Connected', this.logContext());
    this.callStartingLoadingState$.next(false);
    return { call, status: 'connected' };
  }

  checkDebriefInterviewDetails(): Promise<void> {
    const callId = this.activeCallId;
    const questionsList = this.assistantOveride?.variableValues?.questionsList;

    if (!callId || typeof questionsList !== 'string' || !questionsList.trim()) {
      console.error('Cannot check debrief interview details: call ID or questions list is missing.');
      Sentry.logger.error(
        'Debrief Interview Details Payload Missing',
        this.logContext(),
      );
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      // Caps how long the candidate is held on the bot page. This only stops
      // the wait - the keepalive request keeps running until the server answers.
      const maxWaitTimer = setTimeout(() => {
        console.warn('Debrief interview details check is slow; continuing without waiting.');
        Sentry.logger.warn('Debrief Interview Details Check Slow', { callId });
        resolve();
      }, 15000);
      const settle = () => {
        clearTimeout(maxWaitTimer);
        resolve();
      };

      this._examinationService
        .checkDebriefInterviewDetails({ callId, questionsList })
        .subscribe({
          next: (response) => {
            if (response?.statusCode !== 200) {
              console.error('Failed to check debrief interview details:', response);
              Sentry.logger.error('Debrief Interview Details Check Failed', {
                callId,
                statusCode: String(response?.statusCode),
              });
            }
          },
          error: (error) => {
            console.error('Failed to check debrief interview details:', error);
            Sentry.captureException(error, {
              tags: { stage: 'debrief-interview-details-check', callId },
            });
            settle();
          },
          complete: () => settle(),
        });
    });
  }

  private getGreetingMessage(): string {
    const currentHour = moment().hour();
    if (currentHour < 12) return 'Good morning';
    if (currentHour < 18) return 'Good afternoon';
    return 'Good evening';
  }

  stopVapiCall(reason: string = 'unknown') {
    if (!this.vapi) return;
    const effectiveReason = this.callEndedNaturally
      ? 'call-ended-naturally'
      : reason;
    Sentry.logger.info(
      'Interview Bot Call Stopped',
      this.logContext({ reason: effectiveReason }),
    );
    this.vapi.stop();
    this.activeCallId = null;
    this.vapi.removeAllListeners();
    this.eventsRegistered = false;
    this.vapi = null;
  }

  sendMessageToVapi(message: string) {
    if (!message?.trim()) return;
    // if (!this.isOngoingCall$.getValue()) {
    //   console.warn('No active call. Cannot send message.');
    //   return;
    // }

    this.vapi.send({
      type: 'add-message',
      message: {
        role: 'user',
        content: message,
      },
      triggerResponseEnabled: true,
    });
    setTimeout(() => {
      console.log('acknowledging');
      this.vapi.send({
        type: 'add-message',
        message: {
          role: 'system',
          content: 'please acknowledge the message user have just sent',
        },
        triggerResponseEnabled: true,
      });
    }, 1000);
  }

  public sendFirstUserMessageEvent(message: string) {
    console.log(message, 'message22');
    // This is now handled by setupMessagesMonitoring, so this method can be a no-op
    // or kept for backward compatibility
  }

  exitFullScreen() {
    if (this.document.exitFullscreen) {
      this.document.exitFullscreen();
    } else if (this.document.mozCancelFullScreen) {
      this.document.mozCancelFullScreen();
    } else if (this.document.webkitExitFullscreen) {
      this.document.webkitExitFullscreen();
    } else if (this.document.msExitFullscreen) {
      this.document.msExitFullscreen();
    }
  }

  private checkQueueStatus(callId: string): Observable<string> {
    console.log('Checking queue status for call ID:', callId);
    return interval(5000).pipe(
      take(2),
      switchMap(() => this._examinationService.interviewCallStatus(callId)),
      map((res) => res.status),
    );
  }

   handleStatusUpdate(msg: any): void {
    if (msg.status === 'ended') {
      Sentry.logger.warn(
        'Interview Bot Status Update: Ended',
        this.logContext({ endedReason: String(msg.endedReason) }),
      );
    }

    if (msg.status === 'ended' && msg.endedReason === 'silence-timed-out') {
      this.isBotEndedDueToSilence = true;
      this.sendWebEvent(
        'InterviewEndedDueToSilence',
        'Interview ended automatically due to prolonged silence. No response detected from candidate.',
      );
    }
  }

  public setIsDefaultView(isDefaultView: boolean) {
  this.isDefaultViewSubject.next(isDefaultView);
}

handleUploadedDocument(fileUrl: string, extractedText: string): void {
  const current = this.messages$.getValue();

  // ✅ 1. Show file in chat
  this.messages$.next([
    ...current,
    {
      role: 'user',
      type: 'file',
      text: '',
      attachment: {
        name: fileUrl.split('/').pop(),
        url: fileUrl,
        uploadedFile: true
      }
    }
  ]);

  // ✅ 2. Trim large text (VERY IMPORTANT)
  const trimmedText = extractedText.substring(0, 8000);

  // ✅ 3. Send to VAPI
  this.vapi.send({
    type: 'add-message',
    message: {
      role: 'system',
      content: `User has uploaded JD Reference: ${trimmedText}`
    },
    triggerResponseEnabled: true
  });
}
}
